// ============================================
// SETTINGS PAGE — FAZ 4 (v4.1)
// 3 Tab: Hesap Ayarları, E-posta Şablonları, Sistem
// ============================================

import { createFileRoute } from '@tanstack/react-router';
import { useState, useEffect, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  User,
  Mail,
  Database,
  Save,
  Key,
  Loader2,
  CheckCircle,
  AlertCircle,
  Eye,
  X,
  FileText,
  Clock,
  Server,
  HardDrive,
  Activity,
  Megaphone,
  Upload,
} from 'lucide-react';
import { PageHeader } from '@/components';
import { cn } from '@/utils';
import { adminApi, authApi, advertisementsApi } from '@crew-at-sea/api-client';

export const Route = createFileRoute('/_authenticated/settings/')({
  component: SettingsPage,
});

type SettingsTab = 'account' | 'email-templates' | 'system' | 'advertisement';

function SettingsPage() {
  const [activeTab, setActiveTab] = useState<SettingsTab>('account');

  const tabs: { id: SettingsTab; label: string; icon: typeof User }[] = [
    { id: 'account', label: 'Hesap Ayarları', icon: User },
    { id: 'email-templates', label: 'E-posta Şablonları', icon: Mail },
    { id: 'advertisement', label: 'Reklam Ayarları', icon: Megaphone },
    { id: 'system', label: 'Sistem', icon: Database },
  ];

  return (
    <div className="space-y-6">
      <PageHeader
        title="Ayarlar"
        description="Sistem ve hesap ayarlarını yönetin"
      />

      <div className="flex flex-col lg:flex-row gap-6">
        {/* Yan menü */}
        <div className="w-full lg:w-64 flex-shrink-0">
          <nav className="bg-white rounded-maritime border border-gray-200 overflow-hidden">
            {tabs.map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={cn(
                  'flex items-center gap-3 w-full px-4 py-3 text-sm font-medium transition-colors',
                  activeTab === tab.id
                    ? 'bg-maritime-50 text-maritime-700 border-l-2 border-maritime-600'
                    : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
                )}
              >
                <tab.icon className="h-5 w-5" />
                {tab.label}
              </button>
            ))}
          </nav>
        </div>

        {/* İçerik */}
        <div className="flex-1">
          {activeTab === 'account' && <AccountSettings />}
          {activeTab === 'email-templates' && <EmailTemplateSettings />}
          {activeTab === 'system' && <SystemSettings />}
          {activeTab === 'advertisement' && <AdvertisementSettings />}
        </div>
      </div>
    </div>
  );
}

// ============================================
// Hesap Ayarları (Email + Şifre birleşik)
// ============================================
function AccountSettings() {
  const queryClient = useQueryClient();

  // Profil verisi
  const { data: profileResponse, isLoading } = useQuery({
    queryKey: ['admin', 'profile'],
    queryFn: () => adminApi.getProfile(),
  });
  const profile: any = (profileResponse as any)?.data || profileResponse;

  // Email formu
  const [email, setEmail] = useState('');
  const [emailMessage, setEmailMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  useEffect(() => {
    if (profile) setEmail(profile.email || '');
  }, [profile]);

  const updateEmailMutation = useMutation({
    mutationFn: (data: { email?: string }) => adminApi.updateProfile(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin', 'profile'] });
      setEmailMessage({ type: 'success', text: 'E-posta adresi güncellendi' });
      setTimeout(() => setEmailMessage(null), 3000);
    },
    onError: (error: any) => {
      setEmailMessage({ type: 'error', text: error.response?.data?.message || 'E-posta güncellenemedi' });
    },
  });

  // Şifre formu
  const [passwords, setPasswords] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: '',
  });
  const [pwMessage, setPwMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  const updatePasswordMutation = useMutation({
    mutationFn: (data: { currentPassword: string; newPassword: string }) =>
      authApi.changePassword(data),
    onSuccess: () => {
      setPwMessage({ type: 'success', text: 'Şifre başarıyla güncellendi' });
      setPasswords({ currentPassword: '', newPassword: '', confirmPassword: '' });
      setTimeout(() => setPwMessage(null), 3000);
    },
    onError: (error: any) => {
      setPwMessage({ type: 'error', text: error.response?.data?.message || 'Şifre güncellenemedi' });
    },
  });

  const handlePasswordSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPwMessage(null);

    if (!passwords.currentPassword) {
      setPwMessage({ type: 'error', text: 'Mevcut şifre zorunludur' });
      return;
    }
    if (!passwords.newPassword) {
      setPwMessage({ type: 'error', text: 'Yeni şifre zorunludur' });
      return;
    }
    if (passwords.newPassword !== passwords.confirmPassword) {
      setPwMessage({ type: 'error', text: 'Şifreler eşleşmiyor' });
      return;
    }
    if (passwords.newPassword.length < 8) {
      setPwMessage({ type: 'error', text: 'Şifre en az 8 karakter olmalıdır' });
      return;
    }

    const hasUppercase = /[A-Z]/.test(passwords.newPassword);
    const hasLowercase = /[a-z]/.test(passwords.newPassword);
    const hasNumber = /\d/.test(passwords.newPassword);
    if (!hasUppercase || !hasLowercase || !hasNumber) {
      setPwMessage({ type: 'error', text: 'Şifre en az 1 büyük harf, 1 küçük harf ve 1 rakam içermelidir' });
      return;
    }

    updatePasswordMutation.mutate({
      currentPassword: passwords.currentPassword,
      newPassword: passwords.newPassword,
    });
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-maritime border border-gray-200 p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* E-posta */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">E-posta Adresi</h3>

        <MessageBanner message={emailMessage} />

        <form
          onSubmit={(e) => {
            e.preventDefault();
            updateEmailMutation.mutate({ email });
          }}
          className="space-y-4"
        >
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">E-posta</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
            />
          </div>
          <button
            type="submit"
            disabled={updateEmailMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors disabled:opacity-50"
          >
            {updateEmailMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {updateEmailMutation.isPending ? 'Kaydediliyor...' : 'Kaydet'}
          </button>
        </form>
      </div>

      {/* Şifre Değiştir */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Şifre Değiştir</h3>

        <MessageBanner message={pwMessage} />

        <form onSubmit={handlePasswordSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Mevcut Şifre</label>
            <input
              type="password"
              value={passwords.currentPassword}
              onChange={(e) => setPasswords({ ...passwords, currentPassword: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              autoComplete="current-password"
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre</label>
            <input
              type="password"
              value={passwords.newPassword}
              onChange={(e) => setPasswords({ ...passwords, newPassword: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              autoComplete="new-password"
            />
            <p className="text-xs text-gray-500 mt-1">
              En az 8 karakter, 1 büyük harf, 1 küçük harf, 1 rakam
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yeni Şifre Tekrar</label>
            <input
              type="password"
              value={passwords.confirmPassword}
              onChange={(e) => setPasswords({ ...passwords, confirmPassword: e.target.value })}
              className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              autoComplete="new-password"
            />
          </div>
          <button
            type="submit"
            disabled={updatePasswordMutation.isPending}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors disabled:opacity-50"
          >
            {updatePasswordMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Key className="h-4 w-4" />
            )}
            {updatePasswordMutation.isPending ? 'Güncelleniyor...' : 'Şifreyi Güncelle'}
          </button>
        </form>
      </div>
    </div>
  );
}

// ============================================
// E-posta Şablonları
// ============================================
function EmailTemplateSettings() {
  const [previewModal, setPreviewModal] = useState<{ open: boolean; template: any | null }>({
    open: false,
    template: null,
  });

  // Şablonları getir
  const { data: rawTemplates, isLoading } = useQuery({
    queryKey: ['admin', 'email-templates'],
    queryFn: async () => {
      try {
        const response = await adminApi.getEmailTemplates();
        return (response as any)?.data || response || [];
      } catch {
        return [];
      }
    },
    retry: false,
  });

  const templates: any[] = Array.isArray(rawTemplates) ? rawTemplates : [];

  if (isLoading) {
    return (
      <div className="bg-white rounded-maritime border border-gray-200 p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">E-posta Şablonları</h3>
        <p className="text-sm text-gray-500 mb-6">
          Sistem tarafından gönderilen e-posta şablonları ({templates.length} adet)
        </p>

        {templates.length === 0 ? (
          <div className="text-center py-8 text-gray-500">
            <Mail className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>Henüz e-posta şablonu tanımlanmamış</p>
          </div>
        ) : (
          <div className="space-y-3">
            {templates.map((tpl: any) => (
              <div
                key={tpl.id}
                className="flex items-center justify-between p-4 border border-gray-200 rounded-maritime hover:bg-gray-50 transition-colors"
              >
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-maritime-100 rounded-full">
                    <FileText className="h-4 w-4 text-maritime-600" />
                  </div>
                  <div>
                    <p className="font-medium text-gray-900">{tpl.name || tpl.code}</p>
                    <p className="text-xs text-gray-500">
                      {tpl.description || tpl.subjectTr || 'Açıklama yok'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-2 py-0.5 text-xs font-medium rounded-full ${tpl.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'}`}>
                    {tpl.isActive ? 'Aktif' : 'Pasif'}
                  </span>
                  <span className="text-xs text-gray-400">
                    {tpl.variables?.length || 0} değişken
                  </span>
                  <button
                    onClick={() => setPreviewModal({ open: true, template: tpl })}
                    className="p-2 text-gray-400 hover:text-maritime-600 hover:bg-maritime-50 rounded-md transition-colors"
                    title="Önizle"
                  >
                    <Eye className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Önizleme Modal */}
      {previewModal.open && previewModal.template && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setPreviewModal({ open: false, template: null })} />
          <div className="relative w-full max-w-2xl bg-white rounded-maritime shadow-maritime-lg max-h-[90vh] overflow-hidden flex flex-col">
            <div className="flex items-center justify-between p-4 border-b border-gray-200">
              <div>
                <h3 className="text-lg font-semibold">{previewModal.template.name}</h3>
                <p className="text-xs text-gray-500 mt-1">Konu: {previewModal.template.subjectTr}</p>
              </div>
              <button
                onClick={() => setPreviewModal({ open: false, template: null })}
                className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-full"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-4">
              <div
                className="border rounded-maritime bg-gray-50"
                dangerouslySetInnerHTML={{ __html: previewModal.template.bodyTr || '<p>İçerik yok</p>' }}
              />
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ============================================
// Sistem Ayarları
// ============================================
function SystemSettings() {
  const [maintenanceMode, setMaintenanceMode] = useState(false);

  // Dashboard verilerinden istatistik çek
  const { data: rawDashboard } = useQuery({
    queryKey: ['admin', 'dashboard'],
    queryFn: async () => {
      const response = await adminApi.getDashboard();
      return (response as any)?.data || response;
    },
  });

  const stats = rawDashboard;

  return (
    <div className="space-y-6">
      {/* Sistem Bilgisi */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Sistem Bilgisi</h3>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <InfoRow icon={Server} label="Uygulama" value="Crew At Sea Admin Panel" />
          <InfoRow icon={Activity} label="Versiyon" value="v2.6 — FAZ 4" />
          <InfoRow icon={HardDrive} label="API" value="https://api.crewatsea.com" />
          <InfoRow icon={Clock} label="Zaman Dilimi" value="Europe/Istanbul (GMT+3)" />
        </div>
      </div>

      {/* Veritabanı İstatistikleri */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Veritabanı İstatistikleri</h3>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <DbStatCard label="Toplam Kullanıcı" value={stats?.totalUsers} />
          <DbStatCard label="Denizciler" value={stats?.totalSeafarers} />
          <DbStatCard label="Şirketler" value={stats?.totalCompanies} />
          <DbStatCard label="Toplam İlan" value={stats?.totalJobs} />
          <DbStatCard label="Aktif İlan" value={stats?.activeJobs} />
          <DbStatCard label="Toplam Başvuru" value={stats?.totalApplications} />
          <DbStatCard label="Bekleyen Başvuru" value={stats?.pendingApplications} />
          <DbStatCard label="Toplam Değerlendirme" value={stats?.totalReviews} />
          <DbStatCard label="Bekleyen Doğrulama" value={stats?.pendingVerifications} />
        </div>
      </div>

      {/* Bakım Modu */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Bakım Modu</h3>
        <p className="text-sm text-gray-500 mb-4">
          Bakım modunu aktif ederseniz tüm kullanıcılar bakım sayfasını görür. Sadece admin paneli erişilebilir kalır.
        </p>
        <label className="relative inline-flex items-center cursor-pointer">
          <input
            type="checkbox"
            className="sr-only peer"
            checked={maintenanceMode}
            onChange={(e) => setMaintenanceMode(e.target.checked)}
          />
          <div className="w-11 h-6 bg-gray-200 peer-focus:outline-none peer-focus:ring-4 peer-focus:ring-maritime-300 rounded-full peer peer-checked:after:translate-x-full peer-checked:after:border-white after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-gray-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-red-600" />
          <span className="ml-3 text-sm font-medium text-gray-700">
            {maintenanceMode ? 'Bakım Modu Aktif' : 'Bakım Modu Kapalı'}
          </span>
        </label>
        {maintenanceMode && (
          <div className="mt-3 p-3 bg-red-50 border border-red-200 rounded-maritime">
            <p className="text-sm text-red-700">
              ⚠️ Bakım modu aktif! Kullanıcılar siteye erişemeyecek.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

// ============================================
// Yardımcı Bileşenler
// ============================================

// Mesaj banner'ı
function MessageBanner({ message }: { message: { type: 'success' | 'error'; text: string } | null }) {
  if (!message) return null;
  return (
    <div
      className={cn(
        'flex items-center gap-2 p-3 rounded-maritime mb-4',
        message.type === 'success' ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'
      )}
    >
      {message.type === 'success' ? <CheckCircle className="h-5 w-5" /> : <AlertCircle className="h-5 w-5" />}
      {message.text}
    </div>
  );
}

// Sistem bilgi satırı
function InfoRow({ icon: Icon, label, value }: { icon: typeof Server; label: string; value: string }) {
  return (
    <div className="flex items-center gap-3 p-3 bg-gray-50 rounded-maritime">
      <Icon className="h-5 w-5 text-gray-400" />
      <div>
        <p className="text-xs text-gray-500">{label}</p>
        <p className="text-sm font-medium text-gray-900">{value}</p>
      </div>
    </div>
  );
}

// DB istatistik kartı
function DbStatCard({ label, value }: { label: string; value?: number }) {
  return (
    <div className="p-3 bg-gray-50 rounded-maritime text-center">
      <p className="text-2xl font-bold text-maritime-700">
        {value !== undefined ? value.toLocaleString('tr-TR') : '—'}
      </p>
      <p className="text-xs text-gray-500 mt-1">{label}</p>
    </div>
  );
}

// Reklam Ayarları
function AdvertisementSettings() {
  const queryClient = useQueryClient();
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null);
  const [rawImageSrc, setRawImageSrc] = useState<string | null>(null);
  const [showCropper, setShowCropper] = useState(false);
  const [title, setTitle] = useState('');
  const [targetUrl, setTargetUrl] = useState('');
  const [position, setPosition] = useState(1);
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null);

  // Active ads query
  const { data: adsResponse, isLoading } = useQuery({
    queryKey: ['advertisements', 'active'],
    queryFn: async () => {
      const response = await advertisementsApi.getActive();
      return response.data || [];
    },
  });

  const activeAds = Array.isArray(adsResponse) ? adsResponse : [];

  const uploadMutation = useMutation({
    mutationFn: (formData: FormData) => advertisementsApi.create(formData),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advertisements', 'active'] });
      setMessage({ type: 'success', text: 'Reklam başarıyla yüklendi ve yayına alındı.' });
      setFile(null);
      setPreviewUrl(null);
      setTitle('');
      setTargetUrl('');
      setPosition(1);
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (error: any) => {
      setMessage({ type: 'error', text: error.message || 'Reklam yüklenirken hata oluştu' });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => advertisementsApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['advertisements', 'active'] });
      setMessage({ type: 'success', text: 'Reklam kaldırıldı.' });
      setTimeout(() => setMessage(null), 5000);
    },
    onError: (error: any) => {
      setMessage({ type: 'error', text: error.message || 'Reklam silinirken hata oluştu' });
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      const reader = new FileReader();
      reader.onload = () => {
        setRawImageSrc(reader.result as string);
        setShowCropper(true);
      };
      reader.readAsDataURL(selectedFile);
    }
  };

  const handleCroppedImage = (croppedFile: File) => {
    setFile(croppedFile);
    setPreviewUrl(URL.createObjectURL(croppedFile));
    setShowCropper(false);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!file) {
      setMessage({ type: 'error', text: 'Lütfen bir resim dosyası seçin.' });
      return;
    }

    const formData = new FormData();
    formData.append('file', file);
    formData.append('title', title);
    formData.append('targetUrl', targetUrl);
    formData.append('position', String(position));
    formData.append('isActive', 'true');

    uploadMutation.mutate(formData);
  };

  if (isLoading) {
    return (
      <div className="bg-white rounded-maritime border border-gray-200 p-6 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-maritime-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Current Active Ad Banner */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Yayındaki Aktif Reklamlar ({activeAds.length} adet)</h3>
        
        <MessageBanner message={message} />

        {activeAds.length > 0 ? (
          <div className="space-y-6">
            {activeAds.map((ad: any) => (
              <div key={ad.id} className="p-4 border border-gray-200 rounded-maritime flex flex-col md:flex-row gap-4 items-start md:items-center justify-between">
                <div className="flex flex-col md:flex-row gap-4 items-start md:items-center flex-1">
                  <div className="relative group overflow-hidden border border-gray-200 rounded-maritime max-w-full w-48 flex-shrink-0 bg-gray-50 flex items-center justify-center">
                    <img
                      src={ad.imageUrl.startsWith('http') ? ad.imageUrl : `https://api.crewatsea.com${ad.imageUrl}`}
                      alt={ad.title || 'Reklam Bannerı'}
                      className="max-h-24 object-contain rounded-maritime"
                    />
                  </div>
                  
                  <div className="space-y-1 text-sm flex-1">
                    <div>
                      <span className="font-semibold text-gray-700">Başlık:</span> {ad.title || '—'}
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Gösterim Sırası:</span> <span className="px-2 py-0.5 bg-maritime-100 text-maritime-800 rounded font-semibold text-xs">{ad.position}. Sıra</span>
                    </div>
                    <div>
                      <span className="font-semibold text-gray-700">Yönlendirme Linki:</span>{' '}
                      {ad.targetUrl ? (
                        <a
                          href={ad.targetUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-maritime-600 hover:underline break-all"
                        >
                          {ad.targetUrl}
                        </a>
                      ) : (
                        '—'
                      )}
                    </div>
                  </div>
                </div>

                <button
                  onClick={() => {
                    if (window.confirm('Bu reklamı yayından kaldırmak istediğinize emin misiniz?')) {
                      deleteMutation.mutate(ad.id);
                    }
                  }}
                  disabled={deleteMutation.isPending}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-maritime hover:bg-red-700 transition-colors disabled:opacity-50 text-xs font-semibold"
                >
                  {deleteMutation.isPending ? 'Kaldırılıyor...' : 'Kaldır'}
                </button>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-6 text-gray-500 border-2 border-dashed border-gray-200 rounded-maritime">
            <Megaphone className="w-8 h-8 mx-auto mb-2 text-gray-400" />
            <p>Yayında aktif bir iş ilanı reklamı bulunmuyor.</p>
          </div>
        )}
      </div>

      {/* Upload New Ad Banner Form */}
      <div className="bg-white rounded-maritime border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Yeni Reklam Ekle</h3>
        <p className="text-sm text-gray-500 mb-6">
          Yükleyeceğiniz reklam iş ilanları listesinde seçtiğiniz sırada diğer ilanların genişliğinde görüntülenecektir.
        </p>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Reklam Görseli (Tavsiye edilen: Yatay banner, örn: 800x120px)
            </label>
            <div className="mt-1 flex justify-center px-6 pt-5 pb-6 border-2 border-gray-300 border-dashed rounded-maritime hover:border-maritime-500 transition-colors relative">
              <div className="space-y-1 text-center">
                {previewUrl ? (
                  <div className="relative inline-block">
                    <img src={previewUrl} alt="Önizleme" className="max-h-32 object-contain rounded-maritime" />
                    <button
                      type="button"
                      onClick={() => {
                        setFile(null);
                        setPreviewUrl(null);
                      }}
                      className="absolute -top-2 -right-2 bg-red-600 text-white p-1 rounded-full hover:bg-red-700"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ) : (
                  <>
                    <Upload className="mx-auto h-12 w-12 text-gray-400" />
                    <div className="flex text-sm text-gray-600">
                      <label
                        htmlFor="file-upload"
                        className="relative cursor-pointer bg-white rounded-md font-medium text-maritime-600 hover:text-maritime-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-offset-2 focus-within:ring-maritime-500"
                      >
                        <span>Dosya yükle</span>
                        <input
                          id="file-upload"
                          name="file-upload"
                          type="file"
                          accept="image/*"
                          className="sr-only"
                          onChange={handleFileChange}
                        />
                      </label>
                      <p className="pl-1">veya sürükle bırak</p>
                    </div>
                    <p className="text-xs text-gray-500">PNG, JPG, WEBP maks 10MB</p>
                  </>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Reklam Başlığı (Opsiyonel)</label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="Örn: X Akademi İndirim Fırsatı"
                className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Gösterim Sırası (İş ilanları listesinde kaçıncı sırada gösterilsin?)
              </label>
              <select
                value={position}
                onChange={(e) => setPosition(Number(e.target.value))}
                className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500 bg-white"
              >
                {[...Array(10)].map((_, i) => (
                  <option key={i + 1} value={i + 1}>
                    {i + 1}. İlan Sırası
                  </option>
                ))}
              </select>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Yönlendirme Linki (Opsiyonel)</label>
            <input
              type="url"
              value={targetUrl}
              onChange={(e) => setTargetUrl(e.target.value)}
              placeholder="Örn: https://x-academy.com/camp"
              className="w-full h-10 px-3 border border-gray-300 rounded-maritime focus:outline-none focus:ring-2 focus:ring-maritime-500"
            />
          </div>

          <button
            type="submit"
            disabled={uploadMutation.isPending || !file}
            className="flex items-center gap-2 px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 transition-colors disabled:opacity-50 font-medium"
          >
            {uploadMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" />
            ) : (
              <Save className="h-4 w-4" />
            )}
            {uploadMutation.isPending ? 'Yükleniyor...' : 'Reklamı Kaydet & Yayınla'}
          </button>
        </form>
      </div>

      {showCropper && rawImageSrc && (
        <BannerCropperModal
          imageSrc={rawImageSrc}
          onCrop={handleCroppedImage}
          onClose={() => setShowCropper(false)}
        />
      )}
    </div>
  );
}

interface BannerCropperModalProps {
  imageSrc: string;
  onCrop: (croppedFile: File) => void;
  onClose: () => void;
}

function BannerCropperModal({ imageSrc, onCrop, onClose }: BannerCropperModalProps) {
  const [zoom, setZoom] = useState(1);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
  const [imgElement, setImgElement] = useState<HTMLImageElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const img = new Image();
    img.src = imageSrc;
    img.onload = () => {
      setImgElement(img);
    };
  }, [imageSrc]);

  const handlePointerDown = (e: React.PointerEvent) => {
    setIsDragging(true);
    setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!isDragging) return;
    setPosition({
      x: e.clientX - dragStart.x,
      y: e.clientY - dragStart.y
    });
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    setIsDragging(false);
    e.currentTarget.releasePointerCapture(e.pointerId);
  };

  const handleCrop = () => {
    if (!imgElement || !containerRef.current) return;

    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    const targetWidth = 800;
    const targetHeight = 120;
    canvas.width = targetWidth;
    canvas.height = targetHeight;

    const naturalWidth = imgElement.naturalWidth;
    const naturalHeight = imgElement.naturalHeight;

    const containerWidth = containerRef.current.clientWidth;
    const ratio = targetWidth / containerWidth;

    const dw = targetWidth * zoom;
    const dh = targetWidth * (naturalHeight / naturalWidth) * zoom;

    const dx = (targetWidth / 2) - (dw / 2) + (position.x * ratio);
    const dy = (targetHeight / 2) - (dh / 2) + (position.y * ratio);

    // Fill background with white in case of transparency or margins
    ctx.fillStyle = '#ffffff';
    ctx.fillRect(0, 0, targetWidth, targetHeight);

    ctx.drawImage(
      imgElement,
      0, 0, naturalWidth, naturalHeight, // source coordinates
      dx, dy, dw, dh                     // destination coordinates
    );

    canvas.toBlob((blob) => {
      if (blob) {
        const file = new File([blob], 'cropped-banner.png', { type: 'image/png' });
        onCrop(file);
      }
    }, 'image/png');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
      <div className="bg-white rounded-maritime border border-gray-200 shadow-xl max-w-2xl w-full p-6 space-y-6">
        <div>
          <h3 className="text-lg font-bold text-gray-900">Reklam Görseli Kırpma & Hizalama</h3>
          <p className="text-xs text-gray-500 mt-1">
            Görseli sürükleyerek hizalayın. Yakınlaştırmak veya uzaklaştırmak için alttaki kaydırıcıyı kullanın.
          </p>
        </div>

        {/* Crop Preview Container */}
        <div 
          ref={containerRef}
          className="relative overflow-hidden w-full aspect-[20/3] bg-slate-100 rounded-lg border border-gray-300 cursor-move touch-none select-none"
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
        >
          {imgElement && (
            <img
              src={imageSrc}
              alt="Kırpma Önizleme"
              className="absolute left-1/2 top-1/2 select-none pointer-events-none max-w-none"
              style={{
                width: '100%',
                height: 'auto',
                transform: `translate(calc(-50% + ${position.x}px), calc(-50% + ${position.y}px)) scale(${zoom})`,
              }}
            />
          )}
          
          {/* Grid Overlay for reference */}
          <div className="absolute inset-0 border-2 border-dashed border-white/40 pointer-events-none" />
          <div className="absolute inset-x-0 top-1/3 border-b border-dashed border-white/20 pointer-events-none" />
          <div className="absolute inset-x-0 top-2/3 border-b border-dashed border-white/20 pointer-events-none" />
          <div className="absolute inset-y-0 left-1/3 border-r border-dashed border-white/20 pointer-events-none" />
          <div className="absolute inset-y-0 left-2/3 border-r border-dashed border-white/20 pointer-events-none" />

          {/* Visual indicator tag */}
          <div className="absolute top-2 left-2 bg-black/60 backdrop-blur-sm px-2 py-0.5 rounded text-[9px] font-bold text-white border border-white/10 uppercase tracking-wider pointer-events-none">
            Görünür Alan
          </div>
        </div>

        {/* Zoom Controls */}
        <div className="space-y-2">
          <div className="flex justify-between text-xs text-gray-600 font-medium">
            <span>Ölçeklendirme (Yakınlaştır / Uzaklaştır)</span>
            <span>%{Math.round(zoom * 100)}</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-gray-400">0.5x</span>
            <input
              type="range"
              min="0.5"
              max="4.0"
              step="0.05"
              value={zoom}
              onChange={(e) => setZoom(Number(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-maritime-600"
            />
            <span className="text-xs text-gray-400">4.0x</span>
          </div>
        </div>

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-2">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 border border-gray-300 text-gray-700 rounded-maritime hover:bg-gray-50 text-sm font-medium transition-colors"
          >
            İptal
          </button>
          <button
            type="button"
            onClick={handleCrop}
            className="px-4 py-2 bg-maritime-600 text-white rounded-maritime hover:bg-maritime-700 text-sm font-medium transition-colors"
          >
            Kırp ve Kullan
          </button>
        </div>
      </div>
    </div>
  );
}

